import React                     from 'react';
import { observer }              from 'mobx-react-lite';
import View                      from '@widesk-ui/components/View';
import Button                    from '@widesk-ui/components/Button';
import { CheckOutlined }         from '@ant-design/icons';
import { CloseOutlined }         from '@ant-design/icons';
import { DownOutlined }          from '@ant-design/icons';
import { RocketOutlined }        from '@ant-design/icons';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import appStore                  from '@/stores/appStore';
import shapePlaceId              from '@/TokamapEditor/helpers/shapePlaceId';
import { openPreview }           from '@/TokamapEditor/ux/Header/PreviewModal';
import onboardingStore           from '@/TokamapEditor/stores/onboardingStore';
import type { OnboardingTarget } from '@/TokamapEditor/stores/onboardingStore';
import ToolPreview               from '@/TokamapEditor/ux/ToolPreview';
import OnboardingArrows          from '@/TokamapEditor/ux/OnboardingArrows';
import OnboardingFocus           from '@/TokamapEditor/ux/OnboardingFocus';
import OnboardingShapeAnchor     from '@/TokamapEditor/ux/OnboardingShapeAnchor';
import { ONBOARDING_CARD_CLASS } from '@/TokamapEditor/ux/OnboardingFocus';
import type { ToolPreviewName }  from '@/TokamapEditor/ux/ToolPreview';
import { FOOTER_HEIGHT }         from '@/TokamapEditor/ux/constants';
import useDesignTokens           from '@/hooks/useDesignTokens';
import useIsMobile               from '@/hooks/useIsMobile';

type StepAction = { label: string; onClick: () => void; preview?: ToolPreviewName; primary?: boolean };

type Step = { key: string; title: string; hint: string; done: boolean; target: OnboardingTarget; actions: StepAction[] };

const GAP = 16;
const ZOOM_CONTROLS_CLEARANCE = 60;

type Milestone = 'drawn' | 'named' | 'colored' | 'previewed' | 'deleted' | 'restored';

type Flag = 'started' | 'dismissed' | Milestone;

const MILESTONES: Milestone[] = ['drawn', 'named', 'colored', 'previewed', 'deleted', 'restored'];

const storageKey = (userId: id) => `tokamap.onboarding.user-${userId}`;

const readFlags = (userId: id): Partial<Record<Flag, boolean>> => {
	try {
		return JSON.parse(localStorage.getItem(storageKey(userId)) || '{}') as Partial<Record<Flag, boolean>>;
	} catch {
		return {};
	}
};

const readFlag = (flag: Flag, userId: id) => true === readFlags(userId)[flag];

const writeFlag = (flag: Flag, userId: id) => {
	try {
		localStorage.setItem(storageKey(userId), JSON.stringify({ ...readFlags(userId), [flag]: true }));
	} catch {
		return;
	}
};

const readProgress = (userId: id) => Object.fromEntries(MILESTONES.map(milestone => [milestone, readFlag(milestone, userId)])) as Record<Milestone, boolean>;

const useSteps = (progress: Record<Milestone, boolean>): Step[] => {
	const shapes = currentEditorStore.allShapes;
	const unnamed = shapes.filter(shape => !shapePlaceId(shape)).at(-1);
	const last = shapes.at(-1);

	return [
		{
			key: 'draw',
			title: 'Tracez votre première forme',
			hint: 'Ouvrez « Tracer », choisissez « Rectangle », puis cliquez deux coins opposés sur la carte.',
			done: progress.drawn,
			target: 'draw',
			actions: [],
		},
		{
			key: 'name',
			title: 'Nommez votre forme',
			hint: 'Donnez-lui un nom, puis « Créer » : elle devient un point d’intérêt, avec une fiche que vos visiteurs ouvriront d’un clic.',
			done: progress.named,
			target: 'name',
			actions: unnamed ? [{ label: 'Nommer ma forme', preview: 'placePanel' as const, primary: true, onClick: () => {
				currentEditorStore.requestShapeSelection(unnamed.id);
				currentEditorStore.setPromotingShape(unnamed.id);
			} }] : [],
		},
		{
			key: 'color',
			title: 'Personnalisez sa couleur',
			hint: 'Dans le panneau de la forme, ouvrez l’onglet « Mise en forme », puis « Fond ». Bordure, texte, image ou relief se règlent au même endroit.',
			done: progress.colored,
			target: 'color',
			actions: last ? [{ label: 'Changer sa couleur', primary: true, onClick: () => currentEditorStore.requestShapeSelection(last.id) }] : [],
		},
		{
			key: 'preview',
			title: 'Voyez-la comme un visiteur',
			hint: 'Ouvrez l’« Aperçu », en haut, puis cliquez sur votre forme : sa fiche s’ouvre, comme pour vos visiteurs.',
			done: progress.previewed,
			target: 'preview',
			actions: [{ label: 'Ouvrir l’aperçu', primary: true, onClick: () => {
				onboardingStore.notePreview();
				void openPreview();
			} }],
		},
		{
			key: 'delete',
			title: 'Supprimez-la d’un clic droit',
			hint: 'Faites un clic droit sur votre forme, puis « Supprimer ». Ce menu sert aussi à copier, coller ou verrouiller une forme.',
			done: progress.deleted,
			target: 'delete',
			actions: [],
		},
		{
			key: 'undo',
			title: 'Annulez la suppression',
			hint: 'Une erreur se rattrape toujours : « Annuler », en haut, ou Ctrl + Z, ramène votre forme.',
			done: progress.restored,
			target: 'undo',
			actions: [],
		},
	];
};

const StepMark = (props: { index: number; done: boolean; current: boolean; }) => {
	const tokens = useDesignTokens();

	return (
		<View
			center
			style={{
				width: 22,
				height: 22,
				flexShrink: 0,
				borderRadius: '50%',
				fontSize: 12,
				fontWeight: 700,
				color: props.done ? tokens.surface.card : props.current ? tokens.color.primary : tokens.text.muted,
				background: props.done ? tokens.color.primary : 'transparent',
				border: `2px solid ${props.done || props.current ? tokens.color.primary : tokens.border.default}`,
			}}
		>
			{props.done ? <CheckOutlined style={{ fontSize: 11 }} /> : props.index + 1}
		</View>
	);
};

const OnboardingChecklist = observer(() => {
	const tokens = useDesignTokens();
	const map = currentEditorStore.map;
	const userId = appStore.user.id;
	const ready = !!currentEditorStore._tokamap;
	const [startedEmpty, setStartedEmpty] = React.useState<boolean | null>(null);
	const [dismissed, setDismissed] = React.useState(false);
	const [folded, setFolded] = React.useState(false);
	const collapsed = folded || onboardingStore.drawMenuOpen;
	const [progress, setProgress] = React.useState(() => readProgress(userId));
	const isMobile = useIsMobile();
	const shapes = currentEditorStore.allShapes;

	const reach = (milestone: Milestone) => {
		if (progress[milestone]) return;

		writeFlag(milestone, userId);
		setProgress(previous => ({ ...previous, [milestone]: true }));
	};

	React.useEffect(() => {
		setStartedEmpty(null);
		setDismissed(readFlag('dismissed', userId));
		setProgress(readProgress(userId));
	}, [map?.id, userId]);

	React.useEffect(() => {
		if (!ready || null !== startedEmpty || !map) return;

		const started = readFlag('started', userId) || 0 === currentEditorStore.allShapes.length;

		if (started) writeFlag('started', userId);
		setStartedEmpty(started);
	}, [ready, startedEmpty, map?.id, userId]);

	const previewOpenings = React.useRef(onboardingStore.previewOpenings);

	React.useEffect(() => {
		if (onboardingStore.previewOpenings !== previewOpenings.current) reach('previewed');
	}, [onboardingStore.previewOpenings]);

	const initialBackgrounds = React.useRef(new Map<string, string | undefined>());

	React.useEffect(() => {
		initialBackgrounds.current = new Map();
	}, [map?.id]);

	const steps = useSteps(progress);
	const doneCount = steps.filter(step => step.done).length;
	const finished = doneCount === steps.length;
	const visible = !!map && !!userId && ready && !!startedEmpty && !dismissed && !isMobile;
	const current = steps.findIndex(step => !step.done);
	const target = !visible || finished ? null : steps[current]!.target;
	const doomedShapeId = React.useRef<string | null>(null);
	const deletedShapeId = React.useRef<string | null>(null);
	const undoBaseline = React.useRef<number | null>(null);
	const doomedId = 'delete' === target ? doomedShapeId.current ?? shapes.at(-1)?.id ?? null : null;

	React.useEffect(() => {
		if (0 < shapes.length) reach('drawn');
		if (shapes.some(shape => !!shapePlaceId(shape))) reach('named');

		shapes.forEach(shape => {
			if (!initialBackgrounds.current.has(shape.id)) initialBackgrounds.current.set(shape.id, shape.style?.bgColor);
		});

		if (shapes.some(shape => initialBackgrounds.current.get(shape.id) !== shape.style?.bgColor)) reach('colored');

		if ('delete' !== target) {
			doomedShapeId.current = null;

			return;
		}

		doomedShapeId.current ??= shapes.at(-1)?.id ?? null;

		if (doomedShapeId.current && !shapes.some(shape => shape.id === doomedShapeId.current)) {
			deletedShapeId.current = doomedShapeId.current;
			reach('deleted');
		}
	});

	React.useEffect(() => {
		if ('undo' !== target) {
			undoBaseline.current = null;

			return;
		}

		undoBaseline.current ??= shapes.length;

		const back = deletedShapeId.current && shapes.some(shape => shape.id === deletedShapeId.current);

		if (back || shapes.length > undoBaseline.current) reach('restored');
	});

	React.useEffect(() => {
		if (finished && visible) writeFlag('dismissed', userId);
	}, [finished, visible, userId]);

	React.useEffect(() => {
		onboardingStore.point(target);
	}, [target]);

	React.useEffect(() => () => onboardingStore.point(null), []);

	if (!visible) return null;

	const dismiss = () => {
		writeFlag('dismissed', userId);
		setDismissed(true);
	};

	return (
		<>
			<OnboardingArrows />

			<OnboardingFocus />

			{doomedId && <OnboardingShapeAnchor shapeId={doomedId} />}

			<View
				className={ONBOARDING_CARD_CLASS}
				style={{
					position: 'absolute',
					right: ZOOM_CONTROLS_CLEARANCE,
					bottom: FOOTER_HEIGHT + GAP,
					zIndex: 5,
					width: 340,
					borderRadius: 14,
					overflow: 'hidden',
					background: tokens.surface.card,
					border: `1px solid ${tokens.border.subtle}`,
					boxShadow: tokens.shadow.elevated,
				}}
			>
				<View row centerV gap="sm" px={14} py={12} pointer onClick={() => setFolded(!folded)}>
					<RocketOutlined style={{ fontSize: 16, color: tokens.color.primary }} />

					<View flex>
						<View bold>Premiers pas</View>
						<View size={12} color={tokens.text.secondary}>{finished ? 'Parcours terminé, bravo !' : `${doneCount} sur ${steps.length} étapes`}</View>
					</View>

					<DownOutlined rotate={collapsed ? 180 : 0} style={{ fontSize: 10, color: tokens.text.muted, transition: 'transform .2s ease' }} />

					<View
						center
						pointer
						br={6}
						w={24}
						h={24}
						color={tokens.text.muted}
						hover={{ bg: tokens.surface.subtle }}
						onClick={(event: React.MouseEvent) => {
							event.stopPropagation();
							dismiss();
						}}
					>
						<CloseOutlined style={{ fontSize: 11 }} />
					</View>
				</View>

				<View h={4} style={{ background: tokens.surface.subtle }}>
					<View h={4} style={{ width: `${(doneCount / steps.length) * 100}%`, background: tokens.color.primary, transition: 'width .3s ease' }} />
				</View>

				{!collapsed && finished && (
					<View gap={10} p={16}>
						<View size={15} style={{ fontWeight: 600 }}>Félicitations, vous avez fait le tour !</View>

						<View size={13} color={tokens.text.secondary} style={{ lineHeight: 1.5 }}>
							À vous de jouer : tracez maintenant les formes de votre carte. Quand elle sera prête, « Publier »,
							en haut, la rendra publique.
						</View>

						<View row>
							<Button size="small" onClick={dismiss}>Terminer</Button>
						</View>
					</View>
				)}

				{!collapsed && !finished && (
					<View gap={4} p={10}>
						{steps.map((step, index) => {
							const isCurrent = index === current;

							return (
								<View
									key={step.key}
									row
									gap={10}
									p={8}
									br={10}
									style={{ background: isCurrent ? `color-mix(in srgb, ${tokens.color.primary} 6%, transparent)` : undefined }}
								>
									<StepMark index={index} done={step.done} current={isCurrent} />

									<View flex gap={4} style={{ minWidth: 0 }}>
										<View
											size={13}
											color={step.done ? tokens.text.muted : tokens.text.default}
											style={{ fontWeight: 600, textDecoration: step.done ? 'line-through' : undefined }}
										>
											{step.title}
										</View>

										{isCurrent && (
											<>
												<View size={12} color={tokens.text.secondary} style={{ lineHeight: 1.45 }}>{step.hint}</View>

												{0 < step.actions.length && (
													<View row gap={6} wrap style={{ marginTop: 4 }}>
														{step.actions.map(action => {
															const button = (
																<Button size="small" type={action.primary ? 'primary' : 'default'} onClick={action.onClick}>
																	{action.label}
																</Button>
															);

															return action.preview
																? <ToolPreview key={action.label} name={action.preview} label={action.label}>{button}</ToolPreview>
																: <React.Fragment key={action.label}>{button}</React.Fragment>;
														})}
													</View>
												)}
											</>
										)}
									</View>
								</View>
							);
						})}
					</View>
				)}
			</View>
		</>
	);
});

export default OnboardingChecklist;
